import { AsyncPipe } from "@angular/common";
import {
    Component,
    ElementRef,
    Input,
    ViewChild,
    OnInit,
    ChangeDetectorRef,
    OnDestroy,
    Output,
    EventEmitter,
    inject,
    ChangeDetectionStrategy,
    computed,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";

import { Subscription } from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import {
    MenuType,
    type AboutMenu,
    type Category,
} from "@hermes/api-model-menu";
import {
    getCleanUrlPath,
    MenuAnalyticsService,
    MenuUtilsService,
} from "@hermes/domains-shell-fragments-menu-shared";
import {
    FeatureFlagFacade,
    HEADER_FULL_TRANSPARENT,
} from "@hermes/hermes-web-states-flipper-state";
import { UrlUtils } from "@hermes/utils-generic-services-user-interface";

import { activeMenu } from "../menu-bar.state";

export const MENU_LINK_LVL1 = "menu-link-lvl1";
export const MENU_LINK_LVL2 = "menu-link-lvl2";
export const MENU_LINK_LVL3 = "menu-link-lvl3";
export type MenuLinkLevel =
    | typeof MENU_LINK_LVL1
    | typeof MENU_LINK_LVL2
    | typeof MENU_LINK_LVL3;

@Component({
    selector: "h-menu-bar-link",
    imports: [AsyncPipe],
    templateUrl: "./menu-bar-link.component.html",
    styleUrl: "./menu-bar-link.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuBarLinkComponent implements OnInit, OnDestroy {
    public menuUtilsService = inject(MenuUtilsService);
    public menuAnalyticsService = inject(MenuAnalyticsService);
    private router = inject(Router);
    private featureFlagFacade = inject(FeatureFlagFacade);
    public detector = inject(ChangeDetectorRef);
    public layoutFacade = inject(LayoutFacade);
    public urlUtils = inject(UrlUtils);

    @ViewChild("anchor")
    public anchor!: ElementRef<HTMLAnchorElement>;

    @Input()
    public menuEntry?: Category;

    @Input()
    public menuAboutEntry?: AboutMenu;

    @Input({ required: true })
    public menuLinkLevel!: MenuLinkLevel;

    @Input()
    public primaryEntry?: Category;

    @Input()
    public secondaryEntry?: Category;

    @Input()
    public primaryAboutEntry?: AboutMenu;

    @Input()
    public secondaryAboutEntry?: AboutMenu;

    @Input()
    public anchorId!: string;

    @Output()
    public menuLinkClicked: EventEmitter<void> = new EventEmitter<void>();

    public currentUrl?: string;
    public menuEntryPath?: string;
    public menuAboutEntryPath?: string;

    public isHeaderTransparent = toSignal(
        this.layoutFacade.headerTransparent$,
        { initialValue: false },
    );

    private subscription = new Subscription();

    public isHeaderFullTransparentFF = toSignal(
        this.featureFlagFacade.isActivated(HEADER_FULL_TRANSPARENT),
        { initialValue: false },
    );

    public isAnyParentCategoryActive = computed(() => {
        const active = activeMenu();
        return !!active && active.isActive();
    });

    public shouldShowWhiteText = computed(
        () =>
            this.isHeaderTransparent() &&
            this.isHeaderFullTransparentFF() &&
            !this.isAnyParentCategoryActive(),
    );

    public ngOnInit(): void {
        const path =
            this.menuEntry?.type === MenuType.TYPE_PUSH
                ? this.menuEntry?.categoryPath
                : this.menuEntry?.path || this.menuEntry?.categoryPath;

        this.menuEntryPath = path
            ? this.menuUtilsService.getMenuEntryPathWithoutLocale(path)
            : this.menuEntryPath;

        this.menuAboutEntryPath =
            this.menuAboutEntry && this.menuAboutEntry.data
                ? this.menuUtilsService.getMenuEntryPathWithoutLocale(
                      this.menuAboutEntry.data,
                  )
                : this.menuAboutEntryPath;

        this.currentUrl = getCleanUrlPath(
            this.urlUtils.getCurrentRelativeUrl(),
        );

        this.subscription.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    this.currentUrl = getCleanUrlPath(event.urlAfterRedirects);
                    this.detector.detectChanges();
                }
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public getFocusableElement(): HTMLElement {
        return this.anchor.nativeElement;
    }

    public click(event: MouseEvent | Event): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.menuEntry) {
            this.menuUtilsService.navigateToMenuLink(this.menuEntry);
        }

        if (this.menuAboutEntry) {
            this.menuUtilsService.navigateToAboutMenuLink(this.menuAboutEntry);
        }

        this.menuLinkClicked.emit();

        this.menuAnalyticsService.sendAnalyticsOnMenuLinkClick(
            this.primaryEntry ?? this.primaryAboutEntry,
            this.secondaryEntry ?? this.secondaryAboutEntry,
            this.menuEntry ?? this.menuAboutEntry,
        );
    }

    public isPushMenuEntry(): boolean {
        return this.menuEntry?.type === MenuType.TYPE_PUSH;
    }
}
